/**
 * Утилиты для определения типа устройства и платформы
 */

export interface DeviceInfo {
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    platform: 'iOS' | 'Android' | 'Windows' | 'macOS' | 'Linux' | 'Unknown';
    browser: 'Chrome' | 'Firefox' | 'Safari' | 'Edge' | 'Opera' | 'Unknown';
    deviceType: 'phone' | 'tablet' | 'desktop';
    orientation?: 'portrait' | 'landscape';
    userAgent: string;
    screenWidth: number;
    screenHeight: number;
    // Дополнительная информация о платформе
    platformVersion?: string;
    isIOSDevice?: boolean;
    isAndroidDevice?: boolean;
    isMacDevice?: boolean;
    isWindowsDevice?: boolean;
    // Информация о камере
    cameraSupport: {
        hasFrontCamera: boolean;
        hasBackCamera: boolean;
        supportsFacingMode: boolean;
        supportsMirroring: boolean;
    };
}

/**
 * Определяет тип и характеристики текущего устройства
 */
export function getDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Определение платформы с версией
    const platform = (() => {
        if (/iPad|iPhone|iPod/.test(userAgent)) return 'iOS';
        if (/Android/.test(userAgent)) return 'Android';
        if (/Windows/.test(userAgent)) return 'Windows';
        if (/Mac/.test(userAgent)) return 'macOS';
        if (/Linux/.test(userAgent)) return 'Linux';
        return 'Unknown';
    })() as DeviceInfo['platform'];

    // Определение версии платформы
    const platformVersion = (() => {
        if (platform === 'iOS') {
            const match = userAgent.match(/OS (\d+)_(\d+)/);
            return match ? `${match[1]}.${match[2]}` : undefined;
        }
        if (platform === 'Android') {
            const match = userAgent.match(/Android (\d+\.?\d*)/);
            return match ? match[1] : undefined;
        }
        if (platform === 'Windows') {
            const match = userAgent.match(/Windows NT (\d+\.?\d*)/);
            return match ? match[1] : undefined;
        }
        if (platform === 'macOS') {
            const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
            return match ? match[1].replace('_', '.') : undefined;
        }
        return undefined;
    })();

    // Определение браузера
    const browser = (() => {
        // Проверяем более специфичные браузеры сначала
        if (/Edg(e|A|iOS)/.test(userAgent)) return 'Edge';
        if (/OPR|Opera/.test(userAgent)) return 'Opera';
        // Chrome на iOS определяется по CriOS
        if (/CriOS/.test(userAgent)) return 'Chrome';
        // Firefox на iOS определяется по FxiOS
        if (/FxiOS/.test(userAgent)) return 'Firefox';
        // Chrome на Android/Desktop
        if (/Chrome/.test(userAgent) && !/Edg/.test(userAgent)) return 'Chrome';
        // Firefox на Android/Desktop
        if (/Firefox/.test(userAgent)) return 'Firefox';
        // Safari - только если нет других признаков
        if (/Safari/.test(userAgent)) return 'Safari';
        return 'Unknown';
    })() as DeviceInfo['browser'];

    // Определение типа устройства
    const isMobile = (() => {
        // Проверка User Agent
        const mobileUserAgents = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;
        if (mobileUserAgents.test(userAgent)) return true;

        // Проверка размеров экрана (для мобильных устройств)
        if (windowWidth <= 768) return true;

        // Проверка touch events
        if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
            // Если есть touch support и маленький экран, скорее всего мобильное
            if (windowWidth <= 1024) return true;
        }

        return false;
    })();

    const isTablet = (() => {
        // iPad специфично
        if (/iPad/.test(userAgent)) return true;

        // Android планшеты
        if (/Android/.test(userAgent) && !/Mobile/.test(userAgent)) return true;

        // Планшеты на Windows
        if (/Windows/.test(userAgent) && 'ontouchstart' in window) {
            return windowWidth >= 768 && windowWidth <= 1024;
        }

        // Общая проверка для устройств средних размеров с touch
        if ('ontouchstart' in window && windowWidth >= 768 && windowWidth <= 1024) {
            return true;
        }

        return false;
    })();

    const isDesktop = !isMobile && !isTablet;

    const deviceType: DeviceInfo['deviceType'] = (() => {
        if (isMobile && !isTablet) return 'phone';
        if (isTablet) return 'tablet';
        return 'desktop';
    })();

    // Определение ориентации
    const orientation: DeviceInfo['orientation'] = (() => {
        if (windowWidth > windowHeight) return 'landscape';
        return 'portrait';
    })();

    // Проверка поддержки камеры
    const cameraSupport = (() => {
        const hasMediaDevices = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
        const supportsFacingMode = hasMediaDevices && 'facingMode' in (navigator.mediaDevices.getSupportedConstraints?.() || {});
        const supportsMirroring = platform === 'iOS' || platform === 'Android';

        return {
            hasFrontCamera: hasMediaDevices,
            hasBackCamera: hasMediaDevices && supportsFacingMode,
            supportsFacingMode,
            supportsMirroring
        };
    })();

    return {
        isMobile,
        isTablet,
        isDesktop,
        platform,
        browser,
        deviceType,
        orientation,
        userAgent,
        screenWidth,
        screenHeight,
        platformVersion,
        isIOSDevice: platform === 'iOS',
        isAndroidDevice: platform === 'Android',
        isMacDevice: platform === 'macOS',
        isWindowsDevice: platform === 'Windows',
        cameraSupport
    };
}

/**
 * Простая проверка на мобильное устройство (обратная совместимость)
 */
export function isMobileDevice(): boolean {
    return getDeviceInfo().isMobile;
}

/**
 * Определяет, вероятно ли что устройство будет отправлять повернутое видео
 */
export function isLikelyToNeedVideoRotation(): boolean {
    const deviceInfo = getDeviceInfo();

    // Мобильные устройства часто нуждаются в повороте
    if (deviceInfo.deviceType === 'phone') return true;

    // Планшеты в портретной ориентации тоже могут нуждаться
    if (deviceInfo.deviceType === 'tablet' && deviceInfo.orientation === 'portrait') return true;

    return false;
}

/**
 * Получает предпочтительный угол поворота для устройства
 * @deprecated Используйте getAutoRotationForCamera() вместо этой функции
 * Оставлено только для обратной совместимости и отладки
 */
export function getDefaultRotationForDevice(): 0 | 90 | 180 | 270 {
    const deviceInfo = getDeviceInfo();

    // Для iOS устройств требуется поворот на 270° для портретного видео
    if (deviceInfo.platform === 'iOS') {
        return 270;
    }

    // Android браузеры автоматически корректируют ориентацию
    return 0;
}

/**
 * Определяет нужен ли автоматический поворот для фронтальной камеры
 * ВАЖНО: Приложение работает ТОЛЬКО в портретной ориентации!
 * @param facingMode - режим камеры ('user' для фронтальной, 'environment' для задней)
 * @returns угол поворота (0, 90, 180, 270)
 */
export function getAutoRotationForCamera(facingMode?: 'user' | 'environment'): 0 | 90 | 180 | 270 {
    const deviceInfo = getDeviceInfo();

    // Desktop - не требует поворота
    if (!deviceInfo.isMobile) {
        console.log('🖥️ Desktop detected - no rotation needed');
        return 0;
    }

    // ===== iOS - ВСЕГДА требует поворота для вертикального видео =====
    // Safari на iOS не корректирует ориентацию камеры автоматически
    // Приложение ТОЛЬКО в portrait режиме, поэтому всегда применяем 270°
    // Canvas сохраняет портретные размеры (720×1280) даже после поворота

    if (deviceInfo.platform === 'iOS') {
        console.log(`📱 iOS ${facingMode === 'user' ? 'front' : 'back'} camera - applying 270° rotation for PORTRAIT-ONLY stream`);
        return 270;
    }

    // ===== Android - НЕ требует поворота =====
    // Chrome/Firefox на Android уже корректно обрабатывают ориентацию камеры
    // даже в portrait-only режиме

    if (deviceInfo.platform === 'Android') {
        console.log('🤖 Android - browser handles camera orientation automatically (portrait-only mode)');
        return 0;
    }

    // Для других устройств - не применяем поворот
    console.log('✅ No rotation needed for current device');
    return 0;
}

/**
 * Проверяет, нужно ли применять автоповорот для текущего устройства
 * ВАЖНО: Приложение работает ТОЛЬКО в портретной ориентации!
 */
export function shouldAutoRotateCamera(): boolean {
    const deviceInfo = getDeviceInfo();

    // Автоповорот нужен ТОЛЬКО для iOS устройств
    // Приложение всегда в portrait режиме, поэтому проверка ориентации не нужна
    // Android браузеры (Chrome/Firefox) уже корректно обрабатывают ориентацию камеры
    // Canvas сохраняет портретные размеры даже после поворота
    return deviceInfo.platform === 'iOS';
}

/**
 * Определяет качество видео по умолчанию для устройства
 * ВАЖНО: Приложение работает ТОЛЬКО с телефонами в портретной ориентации!
 * Качество: 720x1280 (ширина x высота для вертикального видео)
 */
export function getDefaultVideoQuality(): { width: number; height: number; fps: number } {
    // ТОЛЬКО портретное видео для телефонов: 720x1280
    // Стриминг доступен только с мобильных телефонов
    return { width: 720, height: 1280, fps: 30 };
}

/**
 * Проверяет поддержку WebRTC функций
 */
export function checkWebRTCSupport() {
    return {
        getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
        RTCPeerConnection: !!(window.RTCPeerConnection || (window as any).webkitRTCPeerConnection),
        mediaRecorder: !!(window.MediaRecorder),
        getDisplayMedia: !!(navigator.mediaDevices && (navigator.mediaDevices as any).getDisplayMedia)
    };
}

/**
 * Получает оптимальные настройки камеры для устройства
 * ВАЖНО: Приложение работает ТОЛЬКО с телефонами в портретной ориентации!
 * Качество: 720x1280 (вертикальное видео)
 */
export function getOptimalCameraSettings(deviceId?: string): MediaStreamConstraints {
    const deviceInfo = getDeviceInfo();

    // Убираем все ограничения по размерам, чтобы увидеть родное разрешение камеры
    const baseConstraints: MediaStreamConstraints = {
        video: {
            // width: { ideal: 720, min: 720 },
            // height: { ideal: 1280, min: 1280 },
            // aspectRatio: { ideal: 9 / 16 }, // Портретная ориентация 9:16
            frameRate: { ideal: 30 }
        },
        audio: false
    };

    // Для iOS убираем все ограничения, чтобы увидеть родное разрешение
    if (deviceInfo.platform === 'iOS') {
        // Убираем все ограничения по размерам
        // (baseConstraints.video as any).width = { ideal: 720 };
        // (baseConstraints.video as any).height = { ideal: 1280 };
        // (baseConstraints.video as any).aspectRatio = { ideal: 9 / 16 };
    }

    // Добавляем facingMode если поддерживается (фронтальная камера по умолчанию)
    if (deviceInfo.cameraSupport.supportsFacingMode) {
        (baseConstraints.video as any).facingMode = 'user';
    }

    // Если указан deviceId, используем его
    if (deviceId) {
        (baseConstraints.video as any).deviceId = { ideal: deviceId };
    }

    return baseConstraints;
}

/**
 * Получает настройки для задней камеры (если поддерживается)
 * ВАЖНО: Приложение работает ТОЛЬКО с телефонами в портретной ориентации!
 * Качество: 720x1280 (вертикальное видео)
 */
export function getBackCameraSettings(deviceId?: string): MediaStreamConstraints {
    // Убираем все ограничения по размерам, чтобы увидеть родное разрешение камеры
    const constraints: MediaStreamConstraints = {
        video: {
            // width: { ideal: 720, min: 720 },
            // height: { ideal: 1280, min: 1280 },
            // aspectRatio: { ideal: 9 / 16 }, // Портретная ориентация 9:16
            frameRate: { ideal: 30 },
            facingMode: 'environment' // Задняя камера
        },
        audio: false
    };

    // Для iOS убираем все ограничения, чтобы увидеть родное разрешение
    const deviceInfo = getDeviceInfo();
    if (deviceInfo.platform === 'iOS') {
        // Убираем все ограничения по размерам
        // (constraints.video as any).width = { ideal: 720 };
        // (constraints.video as any).height = { ideal: 1280 };
        // (constraints.video as any).aspectRatio = { ideal: 9 / 16 };
    }

    if (deviceId) {
        (constraints.video as any).deviceId = { ideal: deviceId };
    }

    return constraints;
}

/**
 * Проверяет, поддерживает ли устройство зеркальное отражение
 */
export function supportsMirroring(): boolean {
    const deviceInfo = getDeviceInfo();
    return deviceInfo.cameraSupport.supportsMirroring;
}

/**
 * Получает информацию о доступных камерах
 */
export async function getAvailableCameras(): Promise<MediaDeviceInfo[]> {
    try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        return devices.filter(device => device.kind === 'videoinput');
    } catch (error) {
        console.error('Error getting camera devices:', error);
        return [];
    }
}

/**
 * Логирует информацию об устройстве для отладки
 */
export function logDeviceInfo(): void {
    const deviceInfo = getDeviceInfo();
    const webrtcSupport = checkWebRTCSupport();

    console.group('🔍 Device Detection Results');
    console.log('📱 Device Type:', deviceInfo.deviceType);
    console.log('💻 Platform:', deviceInfo.platform, deviceInfo.platformVersion ? `(${deviceInfo.platformVersion})` : '');
    console.log('🌐 Browser:', deviceInfo.browser);
    console.log('📐 Orientation:', deviceInfo.orientation);
    console.log('📺 Screen:', `${deviceInfo.screenWidth}x${deviceInfo.screenHeight}`);
    console.log('🎥 Camera Support:', deviceInfo.cameraSupport);
    console.log('🪞 Supports Mirroring:', supportsMirroring());
    console.log('🎥 Likely needs rotation:', isLikelyToNeedVideoRotation());
    console.log('🔄 Default rotation:', getDefaultRotationForDevice() + '°');
    console.log('🔄 Auto rotation (front camera):', getAutoRotationForCamera('user') + '°');
    console.log('🔄 Auto rotation (back camera):', getAutoRotationForCamera('environment') + '°');
    console.log('✅ Should auto-rotate:', shouldAutoRotateCamera());
    console.log('📹 Recommended quality:', getDefaultVideoQuality());
    console.log('🔧 WebRTC Support:', webrtcSupport);
    console.groupEnd();
}
